import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Trophy, MessageSquare, CheckCircle, Star, Sparkles, Ban, TrendingDown, Bot } from "lucide-react";

export default function BookingRequestDetail() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [bid, setBid] = useState({ quoted_price: "", notes: "", valid_until: "" });
  const [confirming, setConfirming] = useState(null); // bid_id being confirmed
  const [countering, setCountering] = useState(null); // bid_id being countered
  const [counterPrice, setCounterPrice] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/booking-requests/${requestId}`).then(({ data }) => setData(data));
  useEffect(() => { load(); }, [requestId]);

  if (!data) return <div className="p-10"><span className="hw-pulse-dot" /></div>;
  const { request, bids } = data;
  const isOwner = user?.role === "customer" && user?.user_id === request.customer_id;
  const isAwarded = request.status === "awarded";
  const winningBidId = request.winning_bid_id;

  const submitBid = async (e) => {
    e.preventDefault();
    if (!bid.quoted_price) return toast.error("Quote required");
    try {
      await api.post(`/booking-requests/${requestId}/bids`, bid);
      toast.success("Bid submitted!");
      setBid({ quoted_price: "", notes: "", valid_until: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startChat = async (b) => {
    try {
      const { data: thread } = await api.post("/chat/start", { hotel_id: b.hotel_id });
      navigate(`/chats/${thread.thread_id}`);
    } catch { toast.error("Chat error"); }
  };

  const acceptBid = async () => {
    if (!confirming) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/booking-requests/${requestId}/bids/${confirming}/accept`);
      toast.success("Offer locked! Complete payment to confirm your booking.");
      setConfirming(null);
      // Take traveller straight to the payment page for the accepted offer price
      navigate(`/pay/booking/${res.booking.booking_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not confirm this bid");
    } finally { setBusy(false); }
  };

  const confirmingBid = bids.find((b) => b.bid_id === confirming);
  const counteringBid = bids.find((b) => b.bid_id === countering);

  const submitCounter = async () => {
    if (!countering) return;
    const price = Number(counterPrice);
    if (!price || price <= 0) return toast.error("Enter a valid counter price");
    setBusy(true);
    try {
      const { data: res } = await api.post(`/booking-requests/${requestId}/bids/${countering}/counter`, { counter_price: price });
      toast.success(res.ai_counter_dispatched ? "Counter sent — AI is drafting a reply for the hotel." : "Counter sent to the hotel.");
      setCountering(null);
      setCounterPrice("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send counter");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="hw-card p-7 hw-shadow-pop">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="hw-label text-blue-700">{request.event_type}</div>
              <h1 className="hw-head text-3xl md:text-4xl font-black text-slate-900 mt-2">{request.city}{request.area ? ` · ${request.area}` : ""} · {request.rooms_needed} rooms</h1>
              <div className="text-slate-600 mt-2">{request.checkin} → {request.checkout}</div>
              {request.notes && <p className="text-slate-700 mt-4">{request.notes}</p>}
              {request.budget_per_room && <div className="hw-label text-slate-500 mt-3">Budget: ₹{request.budget_per_room}/room</div>}
            </div>
            <span
              className={`hw-chip ${isAwarded ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-blue-600 bg-blue-50 text-blue-700"}`}
              data-testid="request-status"
            >
              {isAwarded ? "Awarded" : "Collecting bids"}
            </span>
          </div>
        </div>

        <div className="mt-10">
          <div className="hw-label text-blue-700">Bids ({bids.length})</div>
          <h2 className="hw-head text-3xl font-black text-slate-900 mt-2">Compare offers</h2>
          {isOwner && bids.length > 1 && !isAwarded && (
            <p className="text-sm text-slate-600 mt-1">
              <Sparkles className="w-3.5 h-3.5 inline text-emerald-600" /> Cheapest offer is highlighted. Click <b>Confirm & Book</b> to lock the hotel — all other bids get closed automatically.
            </p>
          )}

          {bids.length === 0 ? (
            <div className="hw-card p-10 mt-5 text-center text-slate-600" data-testid="bids-empty">No bids yet. Hotels in {request.city} are getting notified.</div>
          ) : (
            <div className="grid gap-4 mt-5" data-testid="bids-list">
              {bids.map((b, idx) => {
                const won = isAwarded && b.bid_id === winningBidId;
                const lost = isAwarded && !won;
                const isCheapest = idx === 0 && !isAwarded;
                return (
                  <div
                    key={b.bid_id}
                    className={`hw-card p-6 grid md:grid-cols-12 gap-4 items-center transition-all ${
                      won ? "border-emerald-600 border-2 hw-shadow-pop-amber" :
                      isCheapest ? "border-blue-600 border-2" :
                      lost ? "opacity-60" : ""
                    }`}
                    data-testid={`bid-${b.bid_id}`}
                  >
                    <div className="md:col-span-2">
                      {b.hotel_photo ? (
                        <img src={b.hotel_photo} alt={b.hotel_name} className="w-full h-24 object-cover rounded-sm" />
                      ) : (
                        <div className="w-full h-24 bg-slate-100 rounded-sm" />
                      )}
                    </div>
                    <div className="md:col-span-5">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {won && <span className="hw-chip border-emerald-600 bg-emerald-100 text-emerald-800"><Trophy className="w-3 h-3" /> You picked this</span>}
                        {isCheapest && <span className="hw-chip border-blue-600 bg-blue-50 text-blue-700"><Trophy className="w-3 h-3" /> Best price</span>}
                        {lost && <span className="hw-chip border-slate-300 bg-slate-100 text-slate-500"><Ban className="w-3 h-3" /> Closed</span>}
                        {b.is_ai_generated && <span className="hw-chip border-emerald-300 bg-emerald-50 text-emerald-800 font-bold"><Bot className="w-3 h-3" /> AI-matched</span>}
                        {b.status === "countered" && <span className="hw-chip border-amber-300 bg-amber-50 text-amber-700">You countered ₹{b.counter_price}</span>}
                        {b.hotel_category && <span className="hw-chip text-slate-600 capitalize">{b.hotel_category}</span>}
                      </div>
                      <h3 className="hw-head font-bold text-xl text-slate-900">{b.hotel_name}</h3>
                      <div className="text-sm text-slate-500 flex items-center gap-1">
                        {b.hotel_rating && <span className="inline-flex items-center gap-0.5 text-slate-700"><Star className="w-3 h-3 fill-amber-500 stroke-amber-500" /> {b.hotel_rating}</span>}
                        {b.hotel_rating && " · "}{b.hotel_city}
                      </div>
                      {b.hotel_amenities?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {b.hotel_amenities.slice(0, 5).map((a) => (
                            <span key={a} className="text-[10px] font-bold uppercase bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-sm">{a}</span>
                          ))}
                        </div>
                      )}
                      {b.notes && <p className="text-sm text-slate-700 mt-2 italic">&ldquo;{b.notes}&rdquo;</p>}
                      {b.valid_until && <div className="hw-label text-slate-400 mt-2">Valid until {b.valid_until}</div>}
                    </div>
                    <div className="md:col-span-2">
                      <div className="hw-label text-slate-500">Per room / night</div>
                      <div className="hw-head text-3xl font-black text-slate-900" data-testid={`bid-price-${b.bid_id}`}>₹{b.quoted_price.toLocaleString("en-IN")}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Total ≈ ₹{(b.quoted_price * request.rooms_needed).toLocaleString("en-IN")}</div>
                      {request.budget_per_room && (
                        <div className={`text-[10px] font-bold uppercase mt-1 ${b.quoted_price <= request.budget_per_room ? "text-emerald-700" : "text-red-700"}`}>
                          {b.quoted_price <= request.budget_per_room
                            ? `Under budget by ₹${(request.budget_per_room - b.quoted_price).toLocaleString("en-IN")}`
                            : `Over budget by ₹${(b.quoted_price - request.budget_per_room).toLocaleString("en-IN")}`}
                        </div>
                      )}
                    </div>
                    <div className="md:col-span-3 flex flex-col gap-2">
                      {isOwner && !isAwarded && (
                        <Button
                          onClick={() => setConfirming(b.bid_id)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold"
                          data-testid={`confirm-bid-${b.bid_id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1.5" /> Confirm & Book
                        </Button>
                      )}
                      {isOwner && !isAwarded && b.status !== "countered" && (
                        <Button
                          onClick={() => { setCountering(b.bid_id); setCounterPrice(String(Math.max(0, Math.floor(b.quoted_price * 0.85)))); }}
                          variant="outline"
                          className="rounded-sm border-amber-500 text-amber-700 hover:bg-amber-50"
                          data-testid={`counter-bid-${b.bid_id}`}
                        >
                          <TrendingDown className="w-4 h-4 mr-1.5" /> Counter offer
                        </Button>
                      )}
                      <Button onClick={() => navigate(`/hotels/${b.hotel_slug || b.hotel_id}`)} variant="outline" className="rounded-sm border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white" data-testid={`view-hotel-${b.bid_id}`}>View Hotel</Button>
                      <Button onClick={() => startChat(b)} variant="ghost" className="rounded-sm text-blue-700 hover:text-blue-800" data-testid={`chat-bid-${b.bid_id}`}><MessageSquare className="w-4 h-4 mr-1.5" /> Chat with hotel</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Hotel partner: submit bid (only if not already awarded) */}
          {user?.role === "hotel_partner" && !isAwarded && (
            <form onSubmit={submitBid} className="hw-card p-6 mt-8" data-testid="bid-form">
              <div className="hw-label text-blue-700">Submit a bid</div>
              <h3 className="hw-head text-2xl font-black text-slate-900 mt-2">Win this lead</h3>
              <div className="grid sm:grid-cols-3 gap-3 mt-5">
                <div><Label className="hw-label">Quoted price (₹/room)</Label><Input data-testid="bid-price-input" type="number" value={bid.quoted_price} onChange={(e) => setBid({ ...bid, quoted_price: e.target.value })} className="rounded-sm mt-1" /></div>
                <div><Label className="hw-label">Valid until</Label><Input data-testid="bid-valid-input" type="date" value={bid.valid_until} onChange={(e) => setBid({ ...bid, valid_until: e.target.value })} className="rounded-sm mt-1" /></div>
                <div className="sm:col-span-1"><Label className="hw-label">Notes</Label><Input data-testid="bid-notes-input" value={bid.notes} onChange={(e) => setBid({ ...bid, notes: e.target.value })} placeholder="Includes breakfast" className="rounded-sm mt-1" /></div>
              </div>
              <Button type="submit" className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="submit-bid-btn">Submit Bid</Button>
            </form>
          )}
        </div>
      </div>

      <AlertDialog open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="hw-head font-black text-2xl">Confirm this bid?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">
              You&apos;ll book <b>{confirmingBid?.hotel_name}</b> at <b>₹{confirmingBid?.quoted_price.toLocaleString("en-IN")}/room</b> for {request.rooms_needed} rooms ({request.checkin} → {request.checkout}).
              All other bids will be closed automatically. Payment happens on the next screen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="confirm-cancel">Not yet</AlertDialogCancel>
            <AlertDialogAction onClick={acceptBid} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="confirm-accept">
              {busy ? "Confirming…" : "Yes, book this hotel"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!countering} onOpenChange={(o) => !o && setCountering(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="counter-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="hw-head font-black text-2xl">Counter this bid?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">
              Send a lower offer to <b>{counteringBid?.hotel_name}</b>. Current quote: <b>₹{counteringBid?.quoted_price.toLocaleString("en-IN")}/room</b>.
              {counteringBid?.is_ai_generated && (
                <span className="block mt-2 text-emerald-700 font-bold"><Bot className="w-3.5 h-3.5 inline" /> This hotel uses AI — it&apos;ll draft a smart reply back to you.</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="hw-label">Your counter offer (₹/room)</Label>
            <Input
              type="number"
              min={0}
              value={counterPrice}
              onChange={(e) => setCounterPrice(e.target.value)}
              className="rounded-sm mt-1"
              data-testid="counter-price-input"
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="counter-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submitCounter} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm font-bold" data-testid="counter-submit">
              {busy ? "Sending…" : "Send counter"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
